
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import StyledThemingExample from './styled-theming-example'


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h2>{`Styled Theming`}</h2>
    <p>{`Create themes for your styled components using
`}<a parentName="p" {...{
        "href": "https://github.com/styled-components/styled-theming"
      }}>{`styled-theming`}</a></p>
    <StyledThemingExample mdxType="StyledThemingExample" />
    <p>{`Read the `}<a parentName="p" {...{
        "href": "http://thejameskyle.com/styled-theming.html"
      }}>{`introductory blog post`}</a></p>
    <h3>{`Install`}</h3>
    <p>{`Install the babel-plugin first:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install --save styled-theming
`}</code></pre>
    <h3>{`Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import theme from 'styled-theming'

const boxBackgroundColor = theme('mode', {
  light: '#fff',
  dark: '#000',
})

const Box = styled.div\`
  background-color: \${boxBackgroundColor};
\`

export default function App() {
  return (
    <ThemeProvider theme={{ mode: 'light' }}>
      <Box>Hello World</Box>
    </ThemeProvider>
  )
}
`}</code></pre>
    <h3>{`API`}</h3>
    <h4><inlineCode parentName="h4">{`<ThemeProvider>`}</inlineCode></h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://www.styled-components.com/docs/advanced#theming"
      }}>{`styled-components docs`}</a></p>
    <p><inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` is part of styled-components, but is required for
styled-theming.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { ThemeProvider } from 'styled-components'
`}</code></pre>
    <p><inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` accepts a single prop `}<inlineCode parentName="p">{`theme`}</inlineCode>{` which you should pass an
object with either strings or getter functions. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<ThemeProvider theme={{ mode: "dark", size: "large" }}>
<ThemeProvider theme={{ mode: modes => modes.dark, size: sizes => sizes.large }}>
`}</code></pre>
    <p>{`You should generally set up a `}<inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` at the root of your app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`function App() {
  return (
    <ThemeProvider theme={...}>
      {/* rest of your app */}
    </ThemeProvider>
  );
}
`}</code></pre>
    <h4><inlineCode parentName="h4">{`theme(name, values)`}</inlineCode></h4>
    <p>{`Most of your theming will be done with this function.`}</p>
    <p><inlineCode parentName="p">{`name`}</inlineCode>{` should match one of the keys in your `}<inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`;<ThemeProvider theme={{ whatever: '...' }} />
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`theme("whatever", {...});
`}</code></pre>
    <p><inlineCode parentName="p">{`values`}</inlineCode>{` should be an object where one of the keys will be selected by the
value provided to `}<inlineCode parentName="p">{`<ThemeProvider>`}</inlineCode>{` theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<ThemeProvider theme={{ mode: "light" }} />
<ThemeProvider theme={{ mode: "dark" }} />

theme("mode", {
  light: "...",
  dark: "...",
});
`}</code></pre>
    <p>{`The values of this object can be any CSS value.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`theme("mode", {
  light: "#fff",
  dark: "#000",
});

theme("font", {
  sansSerif: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: 'Georgia, Times, "Times New Roman", serif',
  monoSpaced: "Consolas, monaco, monospace",
});
`}</code></pre>
    <p>{`These values can also be functions that return CSS values.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`theme('mode', {
  light: props => props.theme.userProfileAccentColor.light,
  dark: props => props.theme.userProfileAccentColor.dark,
})
`}</code></pre>
    <p><inlineCode parentName="p">{`theme`}</inlineCode>{` will create a function that you can use as a value in
styled-component's `}<inlineCode parentName="p">{`styled`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import styled from 'styled-components'
import theme from 'styled-theming'

const backgroundColor = theme('mode', {
  light: '#fff',
  dark: '#000',
})

const Box = styled.div\`
  background-color: \${backgroundColor};
\`
`}</code></pre>
    <h4><inlineCode parentName="h4">{`theme.variants(name, prop, themes)`}</inlineCode></h4>
    <p>{`It's often useful to create variants of the same component that are selected
via an additional prop.`}</p>
    <p>{`To make this easier with theming, styled-theming provides a
`}<inlineCode parentName="p">{`theme.variants`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import styled from "styled-components";
import theme from "styled-theming";

const backgroundColor = theme.variants("mode", "variant", {
  default: { light: "gray", dark: "darkgray" },
  primary: { light: "blue", dark: "darkblue" },
  success: { light: "green", dark: "darkgreen" },
  warning: { light: "orange", dark: "darkorange" },
});

const Button = styled.button\`
  background-color: \${backgroundColor};
\`;

Button.propTypes = {
  variant: PropTypes.oneOf(["default", "primary", "success", "warning"])
};

Button.defaultProps = {
  variant: "default",
};

<Button />
<Button variant="primary" />
<Button variant="success" />
<Button variant="warning" />
`}</code></pre>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;